/**
 * /terminos — Términos y Condiciones de Lumfi.
 *
 * Email addresses come from /api/public/support-info so the admin can
 * change them without a redeploy. Have a local lawyer review before
 * enforcing this copy.
 */
import { useLang } from "../../contexts/LangContext";
import { useSupportInfo } from "../../hooks/useSupportInfo";
import { LegalPage, Section } from "./LegalPage";
import { tx } from "../../i18n";

const LAST_UPDATED = "5 de junio de 2026";
const FALLBACK_EMAIL = "hola@lumfi.app";

function EmailLink({ email, fallback = FALLBACK_EMAIL }) {
  const addr = email || fallback;
  return <a href={`mailto:${addr}`}>{addr}</a>;
}

const buildCopy = ({ contactEmail }) => ({
  es: {
    eyebrow: "Legal",
    title: "Términos y Condiciones de Uso",
    intro: (
      <>
        Estos términos rigen el uso de <strong>Lumfi</strong>, una plataforma SaaS operada por
        <strong> Wintime Cash Pilot SAS</strong>. Al crear una cuenta, aceptas estas reglas. Si no
        estás de acuerdo, no uses el servicio. Para dudas:{" "}
        <EmailLink email={contactEmail} />.
      </>
    ),
    sections: [
      {
        id: "que-es",
        title: "1. Qué es Lumfi",
        body: (
          <>
            <p>
              Lumfi es un piloto de flujo de caja diseñado para freelancers e independientes con
              ingresos variables. Te ayuda a entender cuánta plata realmente tienes, cuánto puedes
              gastar esta semana sin romper el mes y qué nivel de riesgo tiene el próximo mes.
            </p>
            <p>
              <strong>Lumfi NO es un asesor financiero, contable ni tributario.</strong> Los números
              que mostramos son cálculos basados <strong>únicamente</strong> en los datos que tu
              ingresas. No te conectamos al banco, no validamos facturas con la DIAN/AFIP/SAT/Receita,
              y no reemplazamos a tu contador. Cualquier decisión financiera la tomas tu, bajo tu
              propio criterio y riesgo.
            </p>
          </>
        ),
      },
      {
        id: "cuenta",
        title: "2. Tu cuenta",
        body: (
          <>
            <ul>
              <li>Debes ser mayor de 18 años (o la edad legal de tu país, si es mayor).</li>
              <li>Eres responsable de mantener la contraseña segura. No la compartas.</li>
              <li>Una cuenta es para una persona. Para equipos pequeños existe el plan Negocio.</li>
              <li>Podemos suspender una cuenta si detectamos uso fraudulento, abuso o violación a estos términos.</li>
            </ul>
          </>
        ),
      },
      {
        id: "planes-pagos",
        title: "3. Planes y pagos",
        body: (
          <>
            <h3>3.1. Plan gratuito</h3>
            <p>
              El plan <strong>Control</strong> es gratis para siempre con un límite generoso. No
              pedimos tarjeta para registrarte.
            </p>
            <h3>3.2. Planes pagos</h3>
            <p>
              Si te suscribes a un plan pago (Pilot, Negocio o add-ons como +WhatsApp / +Telegram), el
              cobro se procesa por <strong>Mercado Pago</strong> en tu moneda local. La suscripción se
              renueva automáticamente cada mes (o cada N meses según el plan) hasta que la canceles.
            </p>
            <h3>3.3. Cambios de precio</h3>
            <p>
              Si subimos el precio de tu plan, te avisaremos <strong>con al menos 30 días de
              anticipación</strong> por email. El precio nuevo aplica desde el próximo ciclo de
              facturación. Tienes derecho a cancelar antes de la renovación sin penalidades.
            </p>
            <h3>3.4. Reembolsos</h3>
            <p>
              Para suscripciones mensuales, no hay reembolso prorrateado al cancelar — pero puedes
              seguir usando el plan hasta que termine el ciclo pagado. Si experimentaste un problema
              técnico grave del lado de Lumfi, contáctanos en{" "}
              <EmailLink email={contactEmail} /> y evaluaremos caso por caso.
            </p>
            <h3>3.5. Impuestos</h3>
            <p>
              Los precios mostrados pueden no incluir impuestos locales (IVA, IIBB, ISS, etc).
              Mercado Pago discrimina el impuesto al momento del cobro según tu país.
            </p>
          </>
        ),
      },
      {
        id: "uso-aceptable",
        title: "4. Uso aceptable",
        body: (
          <>
            <p>No puedes usar Lumfi para:</p>
            <ul>
              <li>Lavado de dinero, evasión fiscal u otra actividad ilegal.</li>
              <li>Registrar datos de terceros sin su consentimiento.</li>
              <li>Hacer ingeniería inversa al sistema, scraping masivo o ataques de cualquier tipo.</li>
              <li>Vender o sublicenciar el acceso a tu cuenta.</li>
              <li>Generar contenido ofensivo, ilegal, difamatorio o discriminatorio a través del chat con LumiBot.</li>
            </ul>
          </>
        ),
      },
      {
        id: "ia",
        title: "5. Uso de inteligencia artificial",
        body: (
          <>
            <p>
              Lumfi usa modelos de lenguaje (OpenAI GPT, Anthropic Claude, Google Gemini) a través de
              la infraestructura de <strong>Emergent</strong> para entender tus mensajes en lenguaje
              natural y convertirlos en movimientos estructurados.
            </p>
            <ul>
              <li>
                <strong>La IA puede equivocarse.</strong> Si LumiBot interpreta mal un mensaje
                (categoría, monto, fecha), revisa el movimiento en la app y corrígelo. La
                responsabilidad final de los datos es tuya.
              </li>
              <li>
                No usamos tus datos financieros para entrenar modelos de IA. Tampoco los proveedores —
                según sus políticas públicas el contenido se elimina del caché en 30 días.
              </li>
              <li>
                Si la IA produce un texto que te parece ofensivo o inapropiado, repórtalo a{" "}
                <EmailLink email={contactEmail} />.
              </li>
            </ul>
          </>
        ),
      },
      {
        id: "propiedad",
        title: "6. Propiedad intelectual",
        body: (
          <>
            <p>
              <strong>Tu contenido es tuyo</strong>. Los movimientos, cobros, deudas, metas e
              inversiones que cargues son de tu propiedad. Lumfi solo tiene una licencia limitada para
              procesarlos y mostrártelos.
            </p>
            <p>
              <strong>Lumfi es nuestro</strong>. El nombre, el logo, el código, la marca, los textos
              del landing, los algoritmos de cálculo, las plantillas de IA y todo lo que veas en
              pantalla son propiedad intelectual de Wintime Cash Pilot SAS. No los copies, modifiques
              ni redistribuyas.
            </p>
          </>
        ),
      },
      {
        id: "disponibilidad",
        title: "7. Disponibilidad del servicio",
        body: (
          <>
            <p>
              Hacemos todo lo posible para que Lumfi esté disponible 24/7, pero <strong>no garantizamos
              una disponibilidad del 100%</strong>. Pueden haber:
            </p>
            <ul>
              <li>Mantenimientos programados (te avisamos con anticipación).</li>
              <li>Caídas inesperadas (las solucionamos lo más rápido posible).</li>
              <li>Interrupciones por terceros (MongoDB, Mercado Pago, Twilio, etc).</li>
            </ul>
            <p>
              Si una caída supera 48 horas continuas, te acreditaremos los días caídos al próximo
              ciclo de facturación, sin que tengas que pedirlo.
            </p>
          </>
        ),
      },
      {
        id: "responsabilidad",
        title: "8. Limitación de responsabilidad",
        body: (
          <>
            <p>
              <strong>Lumfi se ofrece &ldquo;tal cual&rdquo;</strong>, sin garantías de ningún tipo más allá de las
              obligaciones legales mínimas en cada país. En la medida permitida por la ley:
            </p>
            <ul>
              <li>
                Lumfi no es responsable por pérdidas económicas derivadas de decisiones financieras
                basadas en sus cálculos. <strong>Los números son indicativos, no consejo profesional.</strong>
              </li>
              <li>
                Nuestra responsabilidad total acumulada nunca excede lo que hayas pagado a Lumfi en
                los últimos 12 meses (si usas el plan gratuito, la responsabilidad máxima es de USD
                100).
              </li>
              <li>
                No respondemos por daños indirectos, lucro cesante, ni por interrupciones causadas por
                terceros (Mercado Pago, Twilio, Telegram, proveedores de IA).
              </li>
            </ul>
          </>
        ),
      },
      {
        id: "terminacion",
        title: "9. Terminación",
        body: (
          <>
            <h3>9.1. Tu puedes cancelar cuando quieras</h3>
            <p>
              Desde <a href="/my-plan">Mi plan</a> puedes cancelar la suscripción. El acceso continúa
              hasta que termine el ciclo facturado. Para borrar la cuenta completamente, ve a{" "}
              <a href="/profile">Perfil</a> y usa el botón de eliminar — los datos se borran en 30 días.
            </p>
            <h3>9.2. Nosotros podemos terminar el servicio</h3>
            <p>
              Si violas estos términos o si descubrimos uso fraudulento, podemos suspender o terminar
              tu cuenta sin previo aviso. Si la terminación es de nuestro lado y no fue por tu falta,
              te devolvemos lo no consumido.
            </p>
          </>
        ),
      },
      {
        id: "ley",
        title: "10. Ley aplicable y jurisdicción",
        body: (
          <p>
            Estos términos se rigen por las leyes de la República de Colombia. Cualquier disputa se
            resolverá ante los jueces ordinarios de Bogotá D.C., sin perjuicio de los derechos del
            consumidor de tu país de residencia. Si vives en otro país LATAM y la ley local te otorga
            protecciones más fuertes como consumidor, esas prevalecen.
          </p>
        ),
      },
      {
        id: "cambios",
        title: "11. Cambios a estos términos",
        body: (
          <p>
            Si cambiamos estos términos de forma material, te avisaremos por email con{" "}
            <strong>al menos 15 días de anticipación</strong>. Si seguis usando Lumfi después de la
            fecha de vigencia, aceptas la nueva versión. Si no estás de acuerdo, podes cancelar y
            recibir reembolso prorrateado por el período no consumido.
          </p>
        ),
      },
      {
        id: "contacto",
        title: "12. Contacto",
        body: (
          <p>
            <strong>Wintime Cash Pilot SAS</strong><br />
            Email: <EmailLink email={contactEmail} /><br />
            WhatsApp comercial: el número en <a href="/">la home</a>.
          </p>
        ),
      },
    ],
  },
  en: {
    eyebrow: "Legal",
    title: "Terms & Conditions",
    intro: (
      <>
        These terms govern your use of <strong>Lumfi</strong>, a SaaS platform operated by
        <strong> Wintime Cash Pilot SAS</strong>. Creating an account means you accept these rules.
        If you don&apos;t agree, don&apos;t use the service. Questions:{" "}
        <EmailLink email={contactEmail} />.
      </>
    ),
    sections: [
      {
        id: "what-is",
        title: "1. What Lumfi is",
        body: (
          <>
            <p>
              Lumfi is a cash-flow pilot for freelancers and independent workers with variable income.
              It tells you how much money you really have, how much you can spend this week and how
              risky next month looks.
            </p>
            <p>
              <strong>Lumfi is NOT a financial advisor, accountant or tax consultant.</strong> Numbers
              are computed <strong>only</strong> from the data you enter. We don&apos;t connect to your
              bank, don&apos;t validate invoices with tax authorities, and don&apos;t replace your accountant.
              Financial decisions are yours.
            </p>
          </>
        ),
      },
      {
        id: "account",
        title: "2. Your account",
        body: (
          <ul>
            <li>You must be 18+ (or the legal age in your country, whichever is higher).</li>
            <li>Keep your password safe. Don&apos;t share it.</li>
            <li>One account per person. For small teams use the Business plan.</li>
            <li>We may suspend an account for fraud, abuse or terms violations.</li>
          </ul>
        ),
      },
      {
        id: "plans-payments",
        title: "3. Plans & payments",
        body: (
          <>
            <h3>3.1. Free plan</h3>
            <p>The <strong>Control</strong> plan is free forever with generous limits. No card required to sign up.</p>
            <h3>3.2. Paid plans</h3>
            <p>
              If you subscribe to Pilot, Business or add-ons, payment is processed via{" "}
              <strong>Mercado Pago</strong> in your local currency. The subscription renews
              automatically each cycle until cancelled.
            </p>
            <h3>3.3. Price changes</h3>
            <p>We&apos;ll email you <strong>at least 30 days</strong> before any price change. New price applies from the next cycle.</p>
            <h3>3.4. Refunds</h3>
            <p>No prorated refunds on monthly subscriptions, but access continues until the cycle ends. Serious Lumfi-side technical issues are evaluated case by case.</p>
            <h3>3.5. Taxes</h3>
            <p>Prices may not include local taxes. Mercado Pago itemizes them at checkout based on your country.</p>
          </>
        ),
      },
      {
        id: "acceptable-use",
        title: "4. Acceptable use",
        body: (
          <>
            <p>You may not use Lumfi for:</p>
            <ul>
              <li>Money laundering, tax evasion or any illegal activity.</li>
              <li>Logging third-party data without their consent.</li>
              <li>Reverse-engineering, mass-scraping or attacking the service.</li>
              <li>Selling or sublicensing access to your account.</li>
              <li>Generating offensive, illegal or discriminatory content via LumiBot.</li>
            </ul>
          </>
        ),
      },
      {
        id: "ai",
        title: "5. AI usage",
        body: (
          <>
            <p>Lumfi uses LLMs (OpenAI, Anthropic, Google) via Emergent&apos;s infrastructure to interpret your messages.</p>
            <ul>
              <li><strong>AI can make mistakes.</strong> Review interpreted movements and fix any errors. Final accuracy is on you.</li>
              <li>We do not use your financial data to train AI models.</li>
              <li>Report offensive AI output to <EmailLink email={contactEmail} />.</li>
            </ul>
          </>
        ),
      },
      {
        id: "ip",
        title: "6. Intellectual property",
        body: (
          <>
            <p><strong>Your content is yours.</strong> Lumfi has a limited license to process and display it.</p>
            <p><strong>Lumfi belongs to us.</strong> The name, logo, code, brand, landing copy, calculation algorithms and AI templates are property of Wintime Cash Pilot SAS.</p>
          </>
        ),
      },
      {
        id: "availability",
        title: "7. Service availability",
        body: (
          <>
            <p>We aim for 24/7 availability but don&apos;t guarantee 100% uptime. Outages over 48 continuous hours earn an automatic credit on your next invoice.</p>
          </>
        ),
      },
      {
        id: "liability",
        title: "8. Limitation of liability",
        body: (
          <>
            <p>Lumfi is provided &ldquo;as is&rdquo; with the minimum statutory warranties. To the extent permitted by law:</p>
            <ul>
              <li>Lumfi is not liable for financial decisions based on its computations. <strong>Numbers are indicative, not professional advice.</strong></li>
              <li>Our total cumulative liability never exceeds what you paid us in the last 12 months (USD 100 max for free users).</li>
              <li>We&apos;re not liable for indirect damages, lost profits, or third-party outages (Mercado Pago, Twilio, Telegram, AI providers).</li>
            </ul>
          </>
        ),
      },
      {
        id: "termination",
        title: "9. Termination",
        body: (
          <>
            <h3>9.1. You can cancel anytime</h3>
            <p>Cancel from <a href="/my-plan">My plan</a>. Access continues until the cycle ends. To delete everything go to <a href="/profile">Profile</a> — data wiped within 30 days.</p>
            <h3>9.2. We can terminate</h3>
            <p>Terms violations or fraud may lead to immediate suspension. If we terminate without cause, we refund the unused balance.</p>
          </>
        ),
      },
      {
        id: "law",
        title: "10. Governing law",
        body: (
          <p>Colombian law. Disputes resolved in Bogotá D.C. courts, without prejudice to stronger consumer rights in your country.</p>
        ),
      },
      {
        id: "changes",
        title: "11. Changes to these terms",
        body: (
          <p>Material changes get a <strong>15-day</strong> email notice. Continued use = acceptance. Disagree? Cancel and get prorated refund.</p>
        ),
      },
      {
        id: "contact",
        title: "12. Contact",
        body: (
          <p><strong>Wintime Cash Pilot SAS</strong> · <EmailLink email={contactEmail} /></p>
        ),
      },
    ],
  },
  pt: null,
});

export default function Terms() {
  const { lang } = useLang();
  const { data: info } = useSupportInfo();
  const contactEmail = info?.contact_email || null;
  const COPY = buildCopy({ contactEmail });

  const content = COPY[lang] || COPY.es;
  const showPtFallback = lang === "pt" && !COPY.pt;
  return (
    <LegalPage
      title={content.title}
      eyebrow={content.eyebrow}
      lastUpdated={LAST_UPDATED}
      dataTestid="page-legal-terms"
    >
      {showPtFallback && (
        <div
          data-testid="legal-pt-fallback-notice"
          className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm"
        >
          A tradução em português está em andamento — por enquanto, mostramos a versão em espanhol.
        </div>
      )}
      <p className="text-lg text-slate-700 leading-relaxed">{content.intro}</p>
      {/* iter-169zv · Fase 5 — Sticky TOC en mobile/desktop. */}
      {content.sections?.length > 1 && (
        <nav
          aria-label={tx(lang, "Table of contents", "Índice", "Índice")}
          data-testid="legal-toc"
          className="sticky top-2 z-10 -mx-2 my-6 px-2 py-3 bg-white/95 backdrop-blur border border-slate-200 rounded-2xl shadow-sm"
        >
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2 px-2">
            {tx(lang, "Contents", "Índice", "Índice")}
          </p>
          <ol className="flex flex-wrap gap-1.5 px-2">
            {content.sections.map((s, i) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  data-testid={`legal-toc-${s.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium text-slate-700 bg-slate-100 hover:bg-emerald-100 hover:text-emerald-800 transition-colors"
                >
                  <span className="text-slate-400 font-mono text-[10px]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="truncate max-w-[16ch] sm:max-w-none">{s.title}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>
      )}
      {content.sections.map((s) => (
        <Section key={s.id} id={s.id} title={s.title}>
          {s.body}
        </Section>
      ))}
    </LegalPage>
  );
}
